import React, { useState, useEffect } from 'react';
import { X, Reply, Archive, Clock, User, AlertCircle } from 'lucide-react';
import axios from '../api/axios';
import MessageComposer from './MessageComposer';
import styles from './MessageThread.module.css';

const DEFAULT_AVATAR = 'https://storage.tficorg.org/DefaultIcon.png';

const getFullAvatarUrl = (avatarUrl: string | null | undefined): string => {
  if (!avatarUrl) return DEFAULT_AVATAR;
  if (avatarUrl.startsWith('http')) return avatarUrl;
  return `${process.env.REACT_APP_API_URL}${avatarUrl}`;
};

interface Message {
  id: number;
  senderId: number;
  senderName: string;
  senderAvatarUrl?: string;
  recipientId: number;
  recipientName: string;
  recipientAvatarUrl?: string;
  type: number;
  typeName: string;
  priority: number;
  priorityName: string;
  subject: string;
  content: string;
  status: number;
  statusName: string;
  readAt?: string;
  createdAt: string;
  parentMessageId?: number;
}

interface MessageThread {
  message: Message;
  replies: Message[];
}

interface MessageThreadProps {
  messageId: number;
  onClose: () => void;
  onUpdate: () => void;
  currentUserId?: number;
}

const MessageThreadComponent: React.FC<MessageThreadProps> = ({
  messageId,
  onClose,
  onUpdate,
  currentUserId
}) => {
  const [thread, setThread] = useState<MessageThread | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showReplyComposer, setShowReplyComposer] = useState(false);

  useEffect(() => {
    fetchThread();
  }, [messageId]);

  const fetchThread = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`/LeadershipMessages/${messageId}`);
      setThread(response.data);
      
      console.log('Thread loaded:', {
        messageId,
        currentUserId,
        messageSenderId: response.data.message.senderId,
        messageRecipientId: response.data.message.recipientId,
        messageStatus: response.data.message.status,
        replies: response.data.replies?.length || 0
      });
      
      // Mark parent message as read ONLY if current user is the RECIPIENT and it's unread
      if (response.data.message.status === 0 && 
          currentUserId === response.data.message.recipientId) {
        console.log('Marking parent message as read:', messageId);
        try {
          await axios.put(`/LeadershipMessages/${messageId}/read`);
          console.log('Successfully marked parent as read');
        } catch (err) {
          console.error('Failed to mark parent as read:', err);
        }
      }

      // Mark all unread replies as read if current user is recipient
      if (response.data.replies && response.data.replies.length > 0) {
        const unreadReplies = response.data.replies.filter(
          (reply: any) => reply.status === 0 && reply.recipientId === currentUserId
        );
        
        console.log('Found unread replies for current user:', unreadReplies.length);
        
        for (const reply of unreadReplies) {
          try {
            console.log('Marking reply as read:', reply.id);
            await axios.put(`/LeadershipMessages/${reply.id}/read`);
          } catch (err) {
            console.log('Could not mark reply as read:', err);
          }
        }
      }

      onUpdate();
    } catch (err: any) {
      console.error('Error fetching message thread:', err);
      setError(err.response?.data?.message || 'Failed to load message thread');
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async () => {
    if (!thread) return;
    
    try {
      await axios.put(`/LeadershipMessages/${messageId}/archive`);
      onUpdate();
      onClose();
    } catch (err) {
      console.error('Error archiving message:', err);
      setError('Failed to archive message');
    }
  };

  const getPriorityColor = (priority: number) => {
    switch (priority) {
      case 1: return '#3b82f6'; // Normal
      case 2: return '#f59e0b'; // High
      case 3: return '#ef4444'; // Urgent
      default: return '#6b7280';
    }
  };

  const getTypeIcon = (typeName: string) => {
    switch (typeName.toLowerCase()) {
      case 'briefing': return '📋';
      case 'request': return '❓';
      case 'directive': return '📢';
      case 'update': return '📰';
      case 'collaboration': return '🤝';
      default: return '📧';
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return formatDateTime(dateString);
  };

  const renderMessage = (message: Message, isReply: boolean = false) => (
    <div className={`${styles.messageContainer} ${isReply ? styles.reply : ''}`} key={message.id}>
      <div className={styles.messageHeader}>
        <div className={styles.senderInfo}>
          <div className={styles.avatar}>
            <img 
              src={getFullAvatarUrl(message.senderAvatarUrl)} 
              alt={message.senderName}
              onError={(e) => {
                (e.target as HTMLImageElement).src = DEFAULT_AVATAR;
              }}
            />
          </div>
          <div className={styles.senderDetails}>
            <div className={styles.senderName}>{message.senderName}</div>
            <div className={styles.messageTime}>
              <Clock size={14} />
              {formatRelativeTime(message.createdAt)}
            </div>
          </div>
        </div>
        
        {!isReply && (
          <div className={styles.messageMeta}>
            <div className={styles.typeTag}>
              {getTypeIcon(message.typeName)} {message.typeName}
            </div>
            {message.priority > 1 && (
              <div 
                className={styles.priorityTag}
                style={{ backgroundColor: getPriorityColor(message.priority) }}
              >
                {message.priorityName}
              </div>
            )}
          </div>
        )}
      </div>

      {!isReply && (
        <div className={styles.recipientInfo}>
          <User size={14} />
          To: {message.recipientName}
        </div>
      )}

      <div className={styles.messageContent}>
        {!isReply && <div className={styles.subject}>{message.subject}</div>}
        <div className={styles.content}>{message.content}</div>
      </div>

      {message.readAt && (
        <div className={styles.readStatus}>
          Read on {formatDateTime(message.readAt)}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className={styles.overlay} onClick={onClose}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div className={styles.loading}>Loading message...</div>
        </div>
      </div>
    );
  }

  if (error || !thread) {
    return (
      <div className={styles.overlay} onClick={onClose}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div className={styles.error}>
            <AlertCircle size={24} />
            {error || 'Message not found'}
          </div>
          <button className={styles.btnSecondary} onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h2 className={styles.title}>Message Thread</h2>
            {thread.replies.length > 0 && (
              <span className={styles.replyCount}>
                {thread.replies.length} {thread.replies.length === 1 ? 'reply' : 'replies'}
              </span>
            )}
          </div>
          <div className={styles.headerActions}>
            {thread.message.status !== 2 && (
              <button className={styles.btnSecondary} onClick={handleArchive}>
                <Archive size={18} />
                Archive
              </button>
            )}
            <button 
              className={styles.btnPrimary}
              onClick={() => setShowReplyComposer(true)}
            >
              <Reply size={18} />
              Reply
            </button>
            <button className={styles.closeBtn} onClick={onClose}>
              <X size={24} />
            </button>
          </div>
        </div>

        <div className={styles.body}>
          {renderMessage(thread.message)}

          {thread.replies.length > 0 && (
            <div className={styles.repliesSection}>
              <div className={styles.repliesHeader}>
                💬 Replies ({thread.replies.length})
              </div>
              {thread.replies.map((reply) => renderMessage(reply, true))}
            </div>
          )}
        </div>

        {showReplyComposer && (
          <MessageComposer
            onClose={() => setShowReplyComposer(false)}
            onSent={() => {
              fetchThread();
              onUpdate();
            }}
            replyToMessageId={messageId}
            recipientId={currentUserId === thread.message.senderId ? thread.message.recipientId : thread.message.senderId}
            recipientName={currentUserId === thread.message.senderId ? thread.message.recipientName : thread.message.senderName}
            replySubject={thread.message.subject.startsWith('Re: ') ? thread.message.subject : `Re: ${thread.message.subject}`}
          />
        )}
      </div>
    </div>
  );
};

export default MessageThreadComponent;
